import React from 'react';
import styled from 'styled-components';

import { Button } from '@/components/Button/Button';
import { BaseButton } from '@/components/Button/components/BaseButton';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/ui/DropdownMenu';

const SET_UP_LABEL = 'Set up learning teams';

const MANAGE_LABEL = 'Manage learning teams';

/** Mirrors the span the legacy `Button` wraps its label in, since the trigger cannot use one. */
const TriggerLabelStyled = styled.span`
  display: flex;
  align-items: center;
`;

const OptionStyled = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 0;
`;

const OptionTitleStyled = styled.div`
  ${(p) => p.theme.typography.body3};
  color: ${(p) => p.theme.tones.neutral[10]};
`;

const OptionSubtitleStyled = styled.p`
  ${(p) => p.theme.typography.caption};
  color: ${(p) => p.theme.colors.neutral.outline2};
`;

type JourneyLearningTeamsEntryButtonProps = {
  /** Whether the journey already has learning teams, which decides the copy and the behaviour. */
  hasTeams: boolean;
  /** Opens the Learning Teams page on a journey whose teams are already populated. */
  onManage: VoidFunction;
  /** Opens the team-size step that splits the cohort automatically. */
  onRandomize: VoidFunction;
  /** Starts the CSV wizard that places learners by the teams named in a file. */
  onUploadFile: VoidFunction;
};

/**
 * The learning-teams control in the Discussion channels sidebar, and the one gate in front of
 * both ways of populating a cohort.
 *
 * Two states, in the order the admin meets them:
 *
 * - teams already exist — a plain "Manage learning teams" straight to the teams page, since
 *   there is nothing left to choose;
 * - no teams — a menu offering Randomize or Upload file.
 *
 * The trigger is a `BaseButton` and not a `Button`: `asChild` hands the child a ref, which the
 * `Button` wrapper does not forward. `$type` has to be passed explicitly for it to match the
 * "New channel" button above — the wrapper defaults it, while the styled component looks the
 * variant up by key and simply applies nothing when it is missing, leaving the bare `solid`
 * border of the reset to fall back to `currentColor`.
 */
export const JourneyLearningTeamsEntryButton = ({
  hasTeams,
  onManage,
  onRandomize,
  onUploadFile,
}: JourneyLearningTeamsEntryButtonProps): JSX.Element => {
  if (hasTeams) {
    return (
      <Button $fullwidth onClick={onManage}>
        {MANAGE_LABEL}
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <BaseButton type="button" $type="default" $fullwidth aria-label={SET_UP_LABEL}>
          <TriggerLabelStyled>{SET_UP_LABEL}</TriggerLabelStyled>
        </BaseButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent variant="subtle" side="bottom" align="start">
        <DropdownMenuItem variant="subtle" onSelect={onRandomize}>
          <OptionStyled>
            <OptionTitleStyled>Randomize teams</OptionTitleStyled>
            <OptionSubtitleStyled>Learners are split into teams automatically</OptionSubtitleStyled>
          </OptionStyled>
        </DropdownMenuItem>
        <DropdownMenuItem variant="subtle" onSelect={onUploadFile}>
          <OptionStyled>
            <OptionTitleStyled>Upload file</OptionTitleStyled>
            <OptionSubtitleStyled>Learners are placed by the teams in your file</OptionSubtitleStyled>
          </OptionStyled>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
